<template>
  <div>
    <div class="viewer">
      <div class="main">
        <div>
          <router-link class="alt-font" :to="galleryLink">‹ Pattern Gallery</router-link>
        </div>
        <design-view :design="design" :readonly="true"></design-view>

        <div class="download-pdf">
          <download-pdf test="testing" />
        </div>
        <div class="save-or-share">
          <label class="alt-font">Save or Share Design</label>
          <save-form></save-form>
        </div>
      </div>

      <div class="used-colors" v-if="usedColors.length && colorsUsedAndPlaceholders.length === 0 && brand === 'filzfelt'">
        <h3 class="h alt-font">Colors</h3>
        <div class="color-item" v-for=" [key, count, { name, value, idx }]  in  usedColors " :key="key">
          <div class="color-circle" :style="`background: ${value}`"></div>
          <div class="value">{{ count > 1 ? `${count} tiles in ` : `${count} tile in ` }} {{ idx }} {{ name }}</div>
        </div>
      </div>

      <!-- Mosaik tiles: show color name with placeholder or chosen color -->
      <div class="used-colors" v-if="colorsUsedAndPlaceholders.length && brand === 'filzfelt'">
        <h3 class="h alt-font">Colors</h3>
        <div class="color-item" v-for=" { name, usedColor, placeholderColor }  in  colorsUsedAndPlaceholders "
          :key="name">
          <div class="color-circle"
            :style="`background: ${usedColor ? usedColor.value : placeholderColor}; ${usedColor || placeholderColor === 'rgb(255, 255, 255)' ? 'border: 1px solid #000;' : ''}`">
          </div>
          <div class="value">{{ name }} - {{ usedColor ? usedColor.idx : '' }} {{ usedColor ? usedColor.name : 'TBD' }}
          </div>
        </div>
      </div>

      <div class="used-colors" v-if="isSp402 && usedBlocks && brand === 'spinneybeck'">
        <h3 class="h alt-font">Pattern Breakdown</h3>
        <div class="color-item" v-for="( item, index ) in  sp402Breakdown " :key="index">
          <div class="value">({{ item.count }}) {{ item.tile }}</div>
        </div>
      </div>

      <div class="used-colors" v-if="!isSp402 && usedBlocks && brand === 'spinneybeck'">
        <h3 class="h alt-font">Pattern Breakdown</h3>
        <div class="color-item" v-for="( count, name ) in  usedBlocks " :key="name">
          <div class="value">{{ count > 1 ? `${count} tiles in ` : `${count} tile in ` }} {{ name }}</div>
        </div>
      </div>

      <div class="specs">
        <div class="group">
          <h3 class="h alt-font">Wall Dimensions</h3>
          <div class="value">W {{ width }} x H {{ height }}</div>
        </div>
        <div class="group">
          <h3 class="h alt-font">Pattern</h3>
          <div class="value">{{ patternName }}</div>
        </div>
        <div class="group">
          <h3 class="h alt-font">Tile Counts</h3>
          <template v-if="coloredTilesCount">
            <div class="value" v-if="metric">
              W {{ convertInchesToCm(coloredTilesDimensions.width)
              }} cm x H {{ convertInchesToCm(coloredTilesDimensions.height) }} cm
            </div>
            <div class="value" v-else>W {{ inchesToFeetString(coloredTilesDimensions.width) }} x H {{
              inchesToFeetString(coloredTilesDimensions.height) }}</div>

            <div class="value">{{ coloredTilesCount }} tiles</div>
          </template>
          <template v-else>
            <div class="value">{{ totalTilesCount }} total tiles</div>
          </template>
        </div>

        <template v-if="tileDimensions">
          <div class="group">
            <h3 class="h alt-font">Tile Dimensions</h3>
            <div class="value">{{ tileDimensions }}</div>
          </div>
        </template>

        <div class="group">
          <router-link class="alt-font return-back" to="edit">Back to Editor</router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import DesignView from "../components/DesignView";
import DownloadPdf from "../components/DownloadPdf";
import SaveForm from "../components/SaveForm";
import SP_BLOCKS from "../../universal/spinneybeck/blocks";
import FF_BLOCKS from "../../universal/filzfelt/blocks";
import FF_PATTERNS from "../../universal/filzfelt/patterns";
import SP_PATTERNS from "../../universal/spinneybeck/patterns";
import COLORS from "../../universal/filzfelt/colors";
import ffPatterns from "../lib/filzfelt/patterns";
import spPatterns from "../lib/spinneybeck/patterns";
import getSp402Variant from "../lib/spinneybeck/get-sp402-variant";
import { convertInchesToCm, convertInchesToFeet } from '../lib/conversions';
// import { getColorsUsedAndPlaceholders } from '../lib/utils/get-colors-used-and-placeholders';
const { getColorsUsedAndPlaceholders } = require('../lib/utils/get-colors-used-and-placeholders');

const INCHES_IN_METER = 39.3700787;

// START duplicate code for processing pattern 402 (SP)
// NOTE: should refactor code inside src/server/process402Pattern.js to share universally
const sp402VariantNames = require("../../universal/spinneybeck/sp402-variant-names");

const blockByPattern = {};
for (const id of Object.keys(SP_PATTERNS)) {
  const p = SP_PATTERNS[id];
  if (!p.blocks) continue;
  blockByPattern[id] = p.blocks[0].id;
}

const getBlock = patternId => {
  return SP_BLOCKS[blockByPattern[patternId]];
};

const getBlockName = (brand, id) => {
  const block = brand === "filzfelt" ? FF_BLOCKS[id] : SP_BLOCKS[id];
  return block && block.name;
};

function get402variant(data, key) {
  const [i, j, k] = key.split(",").map(n => Number(n));
  const g = (Math.abs(i) + Math.abs(j)) % 2 === 0 ? "0" : "1";
  if (k === 0) return `${g}${k}0`;

  const variant = v => `${g}${k}${v}`;

  switch (k) {
    case 1: // NE
      if (data[`${i + 1},${j - 1},0`]) return variant("3"); // full if has NE neighbor
      if (data[`${i},${j - 1},0`]) return variant("2"); // 1/2v if has N neighbor
      if (data[`${i + 1},${j},0`]) return variant("1"); // 1/2h if has E neighbor
      return variant("0"); // 1/4 if has no neighbors
    case 2: // SE
      if (!data[`${i + 1},${j},0`] && !data[`${i},${j + 1},0`]) {
        return variant("0"); // 1/4 if has no E and S neighbors
      }
      return null; // no block otherwise
    case 3: // SW
      if (data[`${i - 1},${j + 1},0`]) return null; // no block if has SW neighbor
      if (data[`${i},${j + 1},0`]) return variant("2"); // 1/2v if has S neighbor
      if (data[`${i - 1},${j},0`]) return variant("1"); // 1/2h if has W neighbor
      return variant("0"); // 1/4 if has no neighbors
    case 4: // NW
      if (!data[`${i - 1},${j},0`] && !data[`${i},${j - 1},0`]) {
        return variant("0"); // 1/4 if has no W and N neighbors
      }
      return null; // no block otherwise
  }
}

const process402Pattern = (patternId, designData) => {
  const BLOCK = getBlock(patternId);
  if (!BLOCK) return;
  const data = [];
  for (const key of Object.keys(designData)) {
    const block = designData[key];
    const variant = get402variant(designData, key);
    if (variant) {
      const path = BLOCK.variants[variant];
      data.push({ ...block, key, path, variant });
    }
  }
  return data;
};
// END duplicate code for processing pattern 402 (SP)

export default {
  components: {
    DesignView,
    DownloadPdf,
    SaveForm
  },
  computed: {
    ...mapGetters(["sp402Pattern"]),

    design() {
      return this.$store.state.design;
    },

    brand() {
      return this.$store.getters.brand;
    },

    metric() {
      return this.$store.getters.metric;
    },

    galleryLink() {
      const brand = this.brand;
      return { name: "intro", params: { brand } };
    },
    pattern() {
      const pattern =
        this.brand === "filzfelt"
          ? FF_PATTERNS[this.design.pattern]
          : SP_PATTERNS[this.design.pattern];
      return pattern;
    },

    patternName() {
      const pattern = this.pattern;
      return pattern && pattern.name;
    },

    tileDimensions() {
      const pattern =
        this.brand === "filzfelt"
          ? FF_PATTERNS[this.design.pattern]
          : SP_PATTERNS[this.design.pattern];

      return this.metric
        ? pattern && pattern.dimensions && pattern.dimensions.m
        : pattern && pattern.dimensions && pattern.dimensions.i;
    },

    isSp402() {
      return /402/.test(this.patternName);
    },

    sb402Pattern() {
      return process402Pattern(this.design.pattern, this.design.data);
    },

    totalTileCountByDimension() {
      const wallWidthInFeet = parseInt(this.width.split(`'`)[0], 10);
      const wallHeightInFeet = parseInt(this.height.split(`'`)[0], 10);
      return wallWidthInFeet * wallHeightInFeet;
    },

    totalTilesCount() {
      // execption made for Mosaik patterns
      // tiles are 1' x 1' - tile count is the whole wall, not just what has colors
      if (this.pattern.calcTileCountWithDimensions === true) {
        return this.totalTileCountByDimension;
      }
      return Object.keys(this.sp402Pattern || this.design.data).length;
    },

    coloredTilesCount() {
      if (this.pattern.calcTileCountWithDimensions === true) {
        return this.totalTileCountByDimension;
      }
      const colored = Object.values(this.design.data).filter(block => block.color);
      if (this.patternName.startsWith("402")) {
        return Object.entries(this.design.data)
          .filter(([, block]) => block.color)
          .filter(([key]) => getSp402Variant(this.design.data, key)).length;
      }

      return Object.values(this.design.data).filter(block => block.color)
        .length;
    },

    coloredTilesDimensions() {
      const { data, width, height } = this.design;
      let x1 = width;
      let x2 = 0;
      let y1 = height;
      let y2 = 0;
      const extend = (x, y) => {
        if (x < x1) x1 = x;
        if (x > x2) x2 = x;
        if (y < y1) y1 = y;
        if (y > y2) y2 = y;
      };
      const transform = ([px, py], x, y, r) => {
        px += x;
        py += y;
        if (r == null) {
          return [px, py];
        }
        let [a, cx, cy] = r;
        cx += x;
        cy += y;
        a *= Math.PI / 180;
        return [
          cx + Math.cos(a) * (px - cx) - Math.sin(a) * (py - cy),
          cy + Math.sin(a) * (px - cx) + Math.cos(a) * (py - cy)
        ];
      };
      for (const { id, x, y, r, color, points } of Object.values(data)) {
        if (!color) continue;
        if (points) {
          for (const [x, y] of points) {
            extend(x, y);
          }
        } else {
          const { bounds } = FF_BLOCKS[id];
          const [x1, y1, x0 = 0, y0 = 0] = bounds;
          const box = [[x0, y0], [x1, y0], [x1, y1], [x0, y1]];
          for (const p of box) {
            extend(...transform(p, x, y, r));
          }
        }
      }
      if (x1 < 0) x1 = 0;
      if (x2 > width) x2 = width;
      if (y1 < 0) y1 = 0;
      if (y2 > height) y2 = height;
      return {
        width: Math.round(Math.max(x2 - x1, 0)),
        height: Math.round(Math.max(y2 - y1, 0))
      };
    },

    // for mosaik
    colorsUsedAndPlaceholders() {
      return getColorsUsedAndPlaceholders({ design: this.design, brand: this.brand });
    },

    usedColors() {
      const usedSet = new Set();
      let colorUsed = [];
      for (const { color } of Object.values(this.design.data)) {
        if (color) {
          usedSet.add(color);
          colorUsed.push(color);
        }
      }
      const colors = [...usedSet]
        .sort()
        .map(color => [
          color,
          colorUsed.filter(c => c === color).length,
          COLORS[color]
        ]);
      return colors;
    },

    usedBlocks() {
      let tilesUsed = [];
      for (const { name } of Object.values(this.design.data)) {
        if (name) {
          tilesUsed.push(name);
        }
      }
      return tilesUsed.sort().reduce((a, b) => (a[b] = a[b] + 1 || 1) && a, {});
    },

    sp402Breakdown() {
      const breakdown = [];
      const qty = {};
      for (const { id, color, variant } of _.values(this.sb402Pattern)) {
        // let key = getBlockName(this.brand, id);
        // NOTE: replace pattern name with generic "Pattern Element" label for 402
        let key = "Pattern Element";
        if (variant) {
          // 402 quantities
          if (sp402VariantNames[variant]) {
            const sp402key = `${key} ${sp402VariantNames[variant]}`;
            qty[sp402key] = qty[sp402key] + 1 || 1;
          }
        } else {
          qty[key] = qty[key] + 1 || 1;
        }
      }

      Object.keys(qty)
        .sort()
        .forEach(function (v, i) {
          breakdown.push({ tile: v, count: qty[v] });
        });

      return breakdown;
    },

    width() {
      return this.$store.getters.width;
    },

    height() {
      return this.$store.getters.height;
    }
  },

  methods: {
    setPattern(payload) {
      this.$store.commit("setPattern", payload);
    },

    inchesToFeetString(inches) {
      const feetInches = convertInchesToFeet(inches);
      return `${feetInches.feet}'-${feetInches.inches}"`;
    }
  },

  created() {
    if (!this.design.pattern) {
      this.setPattern(
        this.brand === "filzfelt" ? ffPatterns[0].id : spPatterns[0].id
      );
    }
  }
};
</script>

<style scoped>
.viewer {
  display: flex;
  margin: 0 -20px 30px;
}

.viewer>* {
  margin: 0 20px;
}

.main {
  flex: 1 1 auto;
}

.used-colors {
  flex: 0 0 auto;
}

.specs {
  flex: 0 0 170px;
}

.group {
  margin-top: 1em;
}

.h {
  margin: 1.3em 0 0.2em 0;
}

.value {
  padding-left: 0.2em;
}

.color-item {
  display: flex;
  align-items: center;
}

.color-circle {
  flex: 0 0 auto;
  width: 20px;
  height: 20px;
  margin-right: 0.5em;
  border-radius: 20px;
}

.return-back {
  position: relative;
}

.return-back::before {
  position: absolute;
  content: "‹";
  left: -0.6em;
}

.download-pdf,
.save-or-share {
  margin-top: 30px;
}
</style>



// WEBPACK FOOTER //
// View.vue?06a7d847